import { HTTP_CLIENT_KEY, IHTTPClient } from 'common/api/HTTPClient'
import { Container, IInit } from 'common/container/Container'
import { Observable, of } from 'rxjs'
import { CsvContainerConfig } from '../container'
import { IStatusService } from '../../../common/status/StatusService'
import { STATUS_SERVICE_KEY } from '../../../container/app'
import { emptyList, ItemList } from '../../../common/models/ItemList'
import { Csv, CsvDTO, toModel } from '../models/Csv'
import { Query } from '../../../common/api/Query'
import { catchError, map } from 'rxjs/operators'
import { prepareURL } from '../../../common/api/http-helpers'

export interface ICsvApi extends IInit {
  getByID(id: string): Observable<Csv | undefined>

  getFilteredList(q: Query<Csv>): Observable<ItemList<Csv>>

  add(e: CsvDTO): Observable<Csv | undefined>

  update(e: CsvDTO): Observable<Csv | undefined>

  delete(id: string): Observable<boolean>

  uploadFile(patientID: string, file: File): Observable<boolean>
}

export class CsvApi implements ICsvApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as CsvContainerConfig).moduleFullUrl
  }

  getByID(id: string): Observable<Csv | undefined> {
    return this._httpClient.get<Csv>({ url: `${this._url}/${id}` }).pipe(
      map<CsvDTO, Csv>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  getFilteredList(q: Query<Csv>): Observable<ItemList<Csv>> {
    return this._httpClient.get<ItemList<Csv>>({ url: prepareURL(this._url, q) }).pipe(
      map<ItemList<CsvDTO>, ItemList<Csv>>((dto) => {
        const itemList = emptyList<Csv>()
        itemList.count = dto.count
        itemList.items = dto.items.map((d) => toModel(d))
        return itemList
      }),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(emptyList<Csv>())
      })
    )
  }

  add(e: CsvDTO): Observable<Csv | undefined> {
    return this._httpClient.post<Csv>({ url: this._url, body: e }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  update(e: CsvDTO): Observable<Csv | undefined> {
    return this._httpClient.put<Csv>({ url: this._url, body: e }).pipe(
      map<CsvDTO, Csv>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: `${this._url}/${id}` }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  uploadFile(patientID: string, file: File): Observable<boolean> {
    return this._httpClient
      .post<boolean>({
        type: 'form',
        url: `${this._url}/upload`,
        body: { patientID: patientID, csv: file },
      })
      .pipe(
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(false)
        })
      )
  }
}
